import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Plus,
    Edit3,
    Car,
    BadgeCheck,
    Star,
    MapPin,
    ShieldCheck,
    LogOut,
    Gauge,
    Wrench,
    Bell,
    Bookmark,
    Trash2,
    Loader2,
} from "lucide-react";
import { api, absUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import ImageUploader from "@/components/ImageUploader";
import GarageHero from "@/components/GarageHero";
import ListingCard from "@/components/ListingCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { formatPrice, formatMileage } from "@/lib/constants";

/**
 * Garage — 3 sections:
 *   1. My Rides   — vehicles the user owns; each is a mini-profile with a
 *                   for-sale toggle switch (flip to put on market / pull it)
 *   2. For Sale   — active listings the user has posted
 *   3. Wishlist   — saved listings (cars the user wants to buy)
 *
 * Notifications live at /notifications now — not on this page.
 */
const TABS = [
    { key: "rides", label: "My Rides" },
    { key: "forsale", label: "For Sale" },
    { key: "wishlist", label: "Wishlist" },
];

export default function Garage() {
    const { user, logout, updateProfile } = useAuth();
    const [tab, setTab] = useState("rides");
    const [vehicles, setVehicles] = useState([]);
    const [wishlist, setWishlist] = useState([]);
    const [editOpen, setEditOpen] = useState(false);
    const [form, setForm] = useState({ name: "", bio: "", location: "", avatar_url: "" });
    const navigate = useNavigate();

    async function load() {
        try {
            const [{ data: v }, { data: favs }] = await Promise.all([
                api.get("/garage/vehicles"),
                api.get("/favorites"),
            ]);
            setVehicles(v);
            setWishlist(favs?.listings || []);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    useEffect(() => {
        if (user) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    async function toggleForSale(v, next) {
        // Optimistic UI
        setVehicles((cur) =>
            cur.map((x) =>
                x.vehicle_id === v.vehicle_id ? { ...x, for_sale: next } : x
            )
        );
        try {
            const { data } = await api.patch(`/garage/vehicles/${v.vehicle_id}`, {
                for_sale: next,
            });
            setVehicles((cur) =>
                cur.map((x) => (x.vehicle_id === data.vehicle_id ? data : x))
            );
            toast.success(next ? "Put on the market" : "Pulled from the market");
        } catch (e) {
            // rollback
            setVehicles((cur) =>
                cur.map((x) =>
                    x.vehicle_id === v.vehicle_id ? { ...x, for_sale: !next } : x
                )
            );
            toast.error(formatApiError(e));
        }
    }

    async function featureVehicle(v) {
        if (!v.photos?.length) {
            toast.error("Add a photo to this ride first");
            return;
        }
        toast.info("Vinny is rendering your ride into the bay…");
        try {
            const { data } = await api.post(`/garage/vehicles/${v.vehicle_id}/feature`);
            setVehicles((cur) =>
                cur.map((x) => ({
                    ...x,
                    featured: x.vehicle_id === data.vehicle_id,
                    ...(x.vehicle_id === data.vehicle_id ? data : {}),
                }))
            );
            toast.success("Parked in the bay");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function saveProfile() {
        try {
            await updateProfile(form);
            setEditOpen(false);
            toast.success("Profile updated");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    if (!user) return null;
    const featured = vehicles.find((v) => v.featured) || null;

    return (
        <div className="min-h-screen bg-onyx-900">
            {/* Static garage lobby image with optional featured car render */}
            <GarageHero featured={featured} />

            {/* Owner strip */}
            <div className="max-w-3xl mx-auto px-4 mt-4 relative z-10">
                <div
                    className="surface p-4 flex items-center gap-3"
                    style={{ borderRadius: 6 }}
                >
                    <div
                        className="w-14 h-14 overflow-hidden shrink-0"
                        style={{
                            borderRadius: 6,
                            background: "#141414",
                            border: "1px solid #262626",
                        }}
                    >
                        {user.avatar_url ? (
                            <img
                                src={absUrl(user.avatar_url)}
                                alt="avatar"
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center headline text-2xl text-onyx-500">
                                {user.name?.[0]}
                            </div>
                        )}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <div className="headline text-lg truncate">{user.name}</div>
                            {user.verified_seller && (
                                <BadgeCheck className="w-4 h-4 text-hellcat shrink-0" />
                            )}
                            {user.role === "admin" && (
                                <span className="label-caps text-hellcat text-[10px]">
                                    Admin
                                </span>
                            )}
                        </div>
                        <div className="text-[11px] text-onyx-500 flex items-center gap-3 mt-0.5 flex-wrap">
                            {user.location && (
                                <span className="flex items-center gap-1">
                                    <MapPin className="w-3 h-3" /> {user.location}
                                </span>
                            )}
                            <span className="flex items-center gap-1">
                                <Star className="w-3 h-3 fill-hellcat text-hellcat" />
                                {user.seller_rating.toFixed(1)} · {user.seller_rating_count}
                            </span>
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            setForm({
                                name: user.name,
                                bio: user.bio || "",
                                location: user.location || "",
                                avatar_url: user.avatar_url || "",
                            });
                            setEditOpen(true);
                        }}
                        className="btn-ghost px-2.5 py-2 text-[11px] font-bold uppercase tracking-widest inline-flex items-center gap-1"
                        style={{ borderRadius: 4 }}
                        data-testid="edit-profile-btn"
                    >
                        <Edit3 className="w-3.5 h-3.5" /> Edit
                    </button>
                </div>
            </div>

            {/* Quick actions — condensed */}
            <div className="max-w-3xl mx-auto px-4 mt-3 flex flex-wrap gap-2">
                <Link
                    to="/garage/vehicles/new"
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-white"
                    style={{
                        borderRadius: 6,
                        background:
                            "linear-gradient(180deg, #ff3b33 0%, #E10600 55%, #8a0400 100%)",
                        boxShadow:
                            "0 6px 18px rgba(225,6,0,0.35), inset 0 1px 0 rgba(255,255,255,0.14)",
                        letterSpacing: "0.06em",
                    }}
                    data-testid="add-vehicle-btn"
                >
                    <Plus className="w-4 h-4" /> Add Vehicle
                </Link>
                <Link
                    to="/notifications"
                    className="btn-ghost inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold"
                    style={{ borderRadius: 6, letterSpacing: "0.06em" }}
                    data-testid="notifications-btn"
                >
                    <Bell className="w-4 h-4" /> Notifications
                </Link>
                {user.role === "admin" && (
                    <Link
                        to="/admin/control"
                        className="btn-ghost inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold"
                        style={{ borderRadius: 6, letterSpacing: "0.06em" }}
                        data-testid="admin-link"
                    >
                        <ShieldCheck className="w-4 h-4" /> Control Panel
                    </Link>
                )}
            </div>

            {/* Section tabs */}
            <div className="max-w-3xl mx-auto px-4 mt-5">
                <div
                    role="tablist"
                    aria-label="Garage sections"
                    className="inline-flex p-1 gap-1"
                    style={{
                        background: "#141414",
                        border: "1px solid #262626",
                        borderRadius: 6,
                    }}
                >
                    {TABS.map((t) => {
                        const isActive = tab === t.key;
                        const count =
                            t.key === "rides"
                                ? vehicles.length
                                : t.key === "forsale"
                                ? myListings.length
                                : wishlist.length;
                        return (
                            <button
                                key={t.key}
                                onClick={() => setTab(t.key)}
                                data-testid={`garage-tab-${t.key}`}
                                className="px-3 py-1.5 text-xs sm:text-sm font-bold inline-flex items-center gap-1.5"
                                style={{
                                    borderRadius: 4,
                                    background: isActive ? "#E10600" : "transparent",
                                    color: isActive ? "#fff" : "rgba(255,255,255,0.7)",
                                    letterSpacing: "0.04em",
                                }}
                            >
                                {t.label}
                                <span
                                    className="text-[10px] px-1.5 py-0.5"
                                    style={{
                                        background: isActive
                                            ? "rgba(255,255,255,0.18)"
                                            : "rgba(255,255,255,0.05)",
                                        color: isActive ? "#fff" : "rgba(255,255,255,0.5)",
                                        borderRadius: 3,
                                    }}
                                >
                                    {count}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Section content */}
            <section className="max-w-3xl mx-auto px-4 py-5">
                <AnimatePresence mode="wait">
                    {tab === "rides" && (
                        <motion.div
                            key="rides"
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -6 }}
                        >
                            {vehicles.length === 0 ? (
                                <EmptyBlock
                                    icon={Car}
                                    title="Add your first ride"
                                    desc="Build your garage so you can flip a switch to sell or pull them off the market."
                                />
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {vehicles.map((v) => (
                                        <MiniProfile
                                            key={v.vehicle_id}
                                            vehicle={v}
                                            onToggle={(next) => toggleForSale(v, next)}
                                            onFeature={() => featureVehicle(v)}
                                        />
                                    ))}
                                </div>
                            )}
                        </motion.div>
                    )}

                    {tab === "wishlist" && (
                        <motion.div
                            key="wishlist"
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -6 }}
                        >
                            {wishlist.length === 0 ? (
                                <EmptyBlock
                                    icon={Bookmark}
                                    title="Nothing saved yet"
                                    desc="Tap the bookmark on any listing to stash it here."
                                />
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {wishlist.map((l) => (
                                        <ListingCard listing={l} key={l.listing_id} />
                                    ))}
                                </div>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </section>

            {/* Logout — moved to bottom, quieter */}
            <section className="max-w-3xl mx-auto px-4 pb-16">
                <button
                    onClick={async () => {
                        await logout();
                        navigate("/");
                    }}
                    className="btn-ghost w-full py-3 text-xs font-bold uppercase tracking-widest inline-flex items-center justify-center gap-2"
                    style={{ borderRadius: 6, letterSpacing: "0.14em" }}
                    data-testid="garage-logout"
                >
                    <LogOut className="w-4 h-4" /> Log out
                </button>
            </section>

            {/* Edit profile dialog */}
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent
                    className="surface border-onyx-600 max-w-md"
                    style={{ borderRadius: 6 }}
                    data-testid="profile-edit-dialog"
                >
                    <DialogHeader>
                        <DialogTitle className="headline text-2xl">Edit profile</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <div className="label-caps mb-1.5">Avatar</div>
                            <ImageUploader
                                value={form.avatar_url ? [form.avatar_url] : []}
                                onChange={(v) => setForm({ ...form, avatar_url: v[0] || "" })}
                                max={1}
                                testid="profile-avatar-uploader"
                            />
                        </div>
                        <label className="flex flex-col gap-1.5">
                            <span className="label-caps">Name</span>
                            <input
                                data-testid="profile-name"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className="w-full bg-onyx-700 border border-onyx-600 px-3 py-2.5 text-sm focus:outline-none focus:border-hellcat"
                                style={{ borderRadius: 4 }}
                            />
                        </label>
                        <label className="flex flex-col gap-1.5">
                            <span className="label-caps">Location</span>
                            <input
                                data-testid="profile-location"
                                value={form.location}
                                onChange={(e) => setForm({ ...form, location: e.target.value })}
                                className="w-full bg-onyx-700 border border-onyx-600 px-3 py-2.5 text-sm focus:outline-none focus:border-hellcat"
                                style={{ borderRadius: 4 }}
                            />
                        </label>
                        <label className="flex flex-col gap-1.5">
                            <span className="label-caps">Bio</span>
                            <textarea
                                data-testid="profile-bio"
                                value={form.bio}
                                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                                className="w-full bg-onyx-700 border border-onyx-600 px-3 py-2.5 text-sm focus:outline-none focus:border-hellcat h-24"
                                style={{ borderRadius: 4 }}
                            />
                        </label>
                        <button
                            onClick={saveProfile}
                            className="btn-red w-full py-3"
                            style={{ borderRadius: 6 }}
                            data-testid="profile-save"
                        >
                            Save
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

/* ---------------------------- Mini profile card --------------------------- */

function MiniProfile({ vehicle: v, onToggle, onFeature }) {
    const forSale = !!v.for_sale;
    const isFeatured = !!v.featured;
    const [featuring, setFeaturing] = React.useState(false);
    async function handleFeature() {
        if (featuring || isFeatured) return;
        setFeaturing(true);
        try {
            await onFeature();
        } finally {
            setFeaturing(false);
        }
    }
    return (
        <motion.article
            layout
            className="surface overflow-hidden group"
            style={{ borderRadius: 6 }}
            data-testid={`ride-mini-${v.vehicle_id}`}
        >
            {/* Photo strip */}
            <Link
                to={`/vehicles/${v.vehicle_id}`}
                className="block relative aspect-[16/9] listing-image overflow-hidden"
            >
                {v.photos?.[0] ? (
                    <img
                        src={absUrl(v.photos[0])}
                        alt={`${v.year} ${v.model_name} ${v.trim}`}
                        className="w-full h-full object-cover group-hover:scale-[1.03]"
                        style={{ transition: "transform 0.4s ease" }}
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-onyx-500 label-caps">
                        No Photo
                    </div>
                )}
                {forSale && (
                    <span
                        className="absolute top-2 left-2 px-2 py-0.5 text-[10px] font-bold uppercase"
                        style={{
                            background: "#E10600",
                            color: "#fff",
                            borderRadius: 3,
                            letterSpacing: "0.14em",
                            boxShadow: "0 4px 12px rgba(225,6,0,0.4)",
                        }}
                    >
                        On Market
                    </span>
                )}
                {/* Feature-in-bay button — top-right of the photo strip */}
                <button
                    type="button"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleFeature();
                    }}
                    data-testid={`feature-btn-${v.vehicle_id}`}
                    disabled={featuring || isFeatured}
                    className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 text-[10px] font-bold"
                    style={{
                        background: isFeatured
                            ? "linear-gradient(180deg, #ff3b33 0%, #E10600 55%, #7a0400 100%)"
                            : "rgba(6,6,6,0.75)",
                        border: isFeatured
                            ? "1px solid #ff8f8a"
                            : "1px solid rgba(255,255,255,0.14)",
                        color: isFeatured ? "#fff" : "rgba(255,255,255,0.9)",
                        borderRadius: 3,
                        backdropFilter: "blur(6px)",
                        letterSpacing: "0.12em",
                        boxShadow: isFeatured
                            ? "0 4px 12px rgba(225,6,0,0.4)"
                            : "none",
                    }}
                    aria-label="Feature this ride in the bay"
                >
                    {featuring ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                        <Star
                            className="w-3 h-3"
                            fill={isFeatured ? "#fff" : "none"}
                            strokeWidth={2.4}
                        />
                    )}
                    {isFeatured ? "FEATURED" : featuring ? "RENDERING…" : "FEATURE"}
                </button>
            </Link>

            {/* Body */}
            <div className="p-3">
                <div className="flex items-start justify-between gap-2">
                    <Link to={`/vehicles/${v.vehicle_id}`} className="min-w-0">
                        <div
                            className="text-sm text-white leading-tight truncate"
                            style={{ fontWeight: 700, letterSpacing: "0.02em" }}
                        >
                            {v.year} {v.model_name}
                        </div>
                        <div className="text-[11px] text-onyx-500 truncate">
                            {v.trim}
                        </div>
                    </Link>
                    {v.price ? (
                        <div
                            className="text-xs font-bold text-white shrink-0"
                            style={{ letterSpacing: "0.02em" }}
                        >
                            {formatPrice(v.price)}
                        </div>
                    ) : null}
                </div>

                {/* Stats row */}
                <div className="mt-2 flex items-center gap-3 text-[11px] text-onyx-500 flex-wrap">
                    {v.mileage > 0 && (
                        <span className="flex items-center gap-1">
                            <Gauge className="w-3 h-3" /> {formatMileage(v.mileage)}
                        </span>
                    )}
                    {v.mods_level && (
                        <span className="flex items-center gap-1">
                            <Wrench className="w-3 h-3" /> {v.mods_level}
                        </span>
                    )}
                </div>

                {/* On/off market switch */}
                <div
                    className="mt-3 flex items-center justify-between gap-2 px-2.5 py-2"
                    style={{
                        background: forSale
                            ? "rgba(225,6,0,0.10)"
                            : "rgba(255,255,255,0.03)",
                        border: forSale
                            ? "1px solid rgba(225,6,0,0.45)"
                            : "1px solid #262626",
                        borderRadius: 4,
                    }}
                >
                    <div className="min-w-0">
                        <div
                            className="text-[10px] font-bold"
                            style={{
                                color: forSale ? "#ff8f8a" : "rgba(255,255,255,0.55)",
                                letterSpacing: "0.16em",
                            }}
                        >
                            {forSale ? "LIVE ON MARKET" : "PULLED"}
                        </div>
                        <div className="text-[10px] text-onyx-500">
                            {forSale ? "Buyers can find this ride" : "Flip to list for sale"}
                        </div>
                    </div>
                    <Switch
                        checked={forSale}
                        onCheckedChange={onToggle}
                        data-testid={`for-sale-toggle-${v.vehicle_id}`}
                        aria-label="Toggle for sale"
                    />
                </div>
            </div>
        </motion.article>
    );
}

function EmptyBlock({ icon: Icon, title, desc }) {
    return (
        <div
            className="p-8 text-center"
            style={{
                background: "#141414",
                border: "1px dashed #262626",
                borderRadius: 6,
            }}
        >
            <Icon className="w-8 h-8 mx-auto text-onyx-500 mb-2" />
            <div
                className="text-white text-base"
                style={{
                    fontFamily: "'Alfa Slab One', serif",
                    fontWeight: 400,
                }}
            >
                {title}
            </div>
            <div className="text-xs text-onyx-500 mt-1 max-w-xs mx-auto">{desc}</div>
        </div>
    );
}
